import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Calendar, Gift, Award, KeyRound, Archive, Trash2, Loader2, User, Scan, MapPin, FileText, CheckCircle, Shield, Building2, Link2, Unlink, CreditCard, AlertTriangle, XCircle, ArrowUpCircle, ArrowDownCircle, Eye, Newspaper } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ConfirmModal from '../../components/admin/ConfirmModal';
import { Button } from '../../components/ui/button';
import Modal from '../../components/ui/Modal';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';

import { formatDateEST, formatDateOnlyEST } from '../../utils/dateUtils';

// Plan configurations - now dynamic, this is fallback
const PLAN_CONFIG = {
  free: { color: '#9CA3AF', bg: '#F3F4F6', label: 'Free', dealLimit: 1 },
  local: { color: '#FF7A1A', bg: '#FFF7ED', label: 'Local', dealLimit: 1 },
  multi: { color: '#6F5BEB', bg: '#EDE9FE', label: 'Multi', dealLimit: 3 },
  // Everything else is a legacy tier kept only for existing rows (admin-only)
  legacy: { color: '#6B7280', bg: '#F3F4F6', label: 'Legacy plan', dealLimit: 1 }
};

// Color mapping for tier themes
const tierColorMap = {
  gray: '#9CA3AF',
  orange: '#FF7A1A',
  purple: '#6F5BEB',
  indigo: '#312E81',
  teal: '#37B3A8',
  blue: '#3B82F6'
};

// Colour + label for a plan key. A `subscription_tiers` row always wins; a key the
// table no longer carries is a pre-relaunch plan and shows as one grey "Legacy plan"
// badge rather than a Mongo-era name (ADR-0009 PD-4).
const planConfigFor = (tierKey, tiers = []) => {
  const row = tiers.find(t => t.tier_key === tierKey);
  if (row) {
    const color = tierColorMap[row.color_theme] || '#9CA3AF';
    return { color, bg: `${color}20`, label: row.name, dealLimit: row.deal_limit };
  }
  return PLAN_CONFIG[tierKey] || PLAN_CONFIG.legacy;
};

// V5 User type badge component with accent colors based on user type
const UserTypeBadge = ({ accountType, role, email, isAdmin: isAdminProp, size = 'normal' }) => {
  // Check multiple conditions for admin status - no hardcoded emails
  const isAdmin = isAdminProp || role === 'admin' || role === 'master_admin' || accountType === 'admin';
  const isAdvertiser = role === 'advertiser' || accountType === 'advertiser';
  const isVenuePartner = role === 'venue_partner';
  
  const sizeClasses = size === 'large' 
    ? 'px-4 py-2 text-sm' 
    : 'px-3 py-1 text-xs';
  
  if (isAdmin) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses} rounded-full font-medium bg-indigo-100 text-indigo-700`}>
        <Shield size={size === 'large' ? 18 : 14} />
        Admin
      </div>
    );
  }
  
  if (isVenuePartner) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses} rounded-full font-medium bg-venue/10 text-venue`}>
        <MapPin size={size === 'large' ? 18 : 14} />
        Venue Partner
      </div>
    );
  }
  
  if (isAdvertiser) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses} rounded-full font-medium bg-business/10 text-business`}>
        <Building2 size={size === 'large' ? 18 : 14} />
        Advertiser
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-2 ${sizeClasses} rounded-full font-medium bg-consumer/10 text-consumer`}>
      <User size={size === 'large' ? 18 : 14} />
      Consumer
    </div>
  );
};

// Subscription Status Badge
const SubscriptionStatusBadge = ({ status }) => {
  const statusConfig = {
    active: { color: '#059669', bg: '#D1FAE5', label: 'Active' },
    pending_cancel: { color: '#F59E0B', bg: '#FEF3C7', label: 'Canceling' },
    canceled: { color: '#EF4444', bg: '#FEE2E2', label: 'Canceled' },
    expired: { color: '#6B7280', bg: '#F3F4F6', label: 'Expired' },
    none: { color: '#9CA3AF', bg: '#F9FAFB', label: 'None' }
  };
  
  const config = statusConfig[status?.toLowerCase()] || statusConfig.none;
  
  return (
    <span 
      className="px-3 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: config.bg, color: config.color }}
    >
      {config.label}
    </span>
  );
};

// Active Subscription Warning Modal
const ActiveSubscriptionWarningModal = ({ isOpen, onClose, subscriptionInfo, action }) => {
  if (!isOpen) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      title={
        <span className="flex items-center gap-3">
          <span className="p-3 rounded-full bg-amber-100">
            <AlertTriangle size={24} className="text-amber-600" aria-hidden="true" />
          </span>
          Active Subscription
        </span>
      }
    >
        <p className="text-gray-600 mb-4">
          This user has an active <span className="font-bold text-purple-600">{subscriptionInfo?.plan_tier?.toUpperCase()}</span> subscription 
          for <span className="font-semibold">{subscriptionInfo?.business_name}</span>.
        </p>
        
        <p className="text-gray-600 mb-4">
          You must cancel their subscription before you can {action} this user.
        </p>
        
        {subscriptionInfo?.status === 'pending_cancel' && subscriptionInfo?.end_date && (
          <div className="p-3 bg-amber-50 rounded-lg mb-4">
            <p className="text-sm text-amber-800">
              Subscription is canceled but still active until <strong>{formatDateOnlyEST(subscriptionInfo.end_date)}</strong>.
            </p>
          </div>
        )}
        
        <div className="flex gap-3">
          <Button onClick={onClose} className="flex-1 rounded-xl" variant="outline">
            Close
          </Button>
        </div>
    </Modal>
  );
};

// Cancel Subscription Modal
const CancelSubscriptionModal = ({ isOpen, onClose, onConfirm, subscriptionInfo, loading }) => {
  const [immediate, setImmediate] = useState(false);
  
  if (!isOpen) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      title={
        <span className="flex items-center gap-3">
          <span className="p-3 rounded-full bg-red-100">
            <XCircle size={24} className="text-red-600" aria-hidden="true" />
          </span>
          Cancel Subscription
        </span>
      }
    >
        <p className="text-gray-600 mb-4">
          Cancel the <span className="font-bold text-purple-600">{subscriptionInfo?.plan_tier?.toUpperCase()}</span> subscription 
          for <span className="font-semibold">{subscriptionInfo?.business_name}</span>?
        </p>
        
        <div className="p-3 bg-gray-50 rounded-lg mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input 
              type="checkbox" 
              checked={immediate}
              onChange={(e) => setImmediate(e.target.checked)}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-gray-800">Cancel Immediately</p>
              <p className="text-sm text-gray-500">
                If unchecked, subscription remains active until end of billing period.
              </p>
            </div>
          </label>
        </div>
        
        <div className="flex gap-3">
          <Button onClick={onClose} className="flex-1 rounded-xl" variant="outline" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={() => onConfirm(immediate)} 
            className="flex-1 rounded-xl"
            style={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
            disabled={loading}
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {immediate ? 'Cancel Now' : 'Cancel at Period End'}
          </Button>
        </div>
    </Modal>
  );
};

// Change Plan Modal
const ChangePlanModal = ({ isOpen, onClose, onConfirm, currentPlan, loading, subscriptionTiers = [] }) => {
  const [selectedPlan, setSelectedPlan] = useState('');

  if (!isOpen) return null;
  
  // Get current plan key normalized
  const currentPlanKey = currentPlan?.toLowerCase() || '';
  
  // Use dynamic tiers if available, otherwise fallback to legacy
  const availableTiers = subscriptionTiers.length > 0 
    ? subscriptionTiers
        .filter(t => t.tier_key !== currentPlanKey && t.is_enabled)
        .sort((a, b) => a.sort_order - b.sort_order)
    : ['free', 'local', 'multi']
        .filter(p => p !== currentPlanKey)
        .map(key => ({ tier_key: key, name: (PLAN_CONFIG[key] || PLAN_CONFIG.legacy).label, sort_order: ['free', 'local', 'multi'].indexOf(key) }));
  
  const getTierConfig = (tierKey) => {
    const dynamicTier = subscriptionTiers.find(t => t.tier_key === tierKey);
    if (dynamicTier) {
      return {
        color: tierColorMap[dynamicTier.color_theme] || '#9CA3AF',
        bg: `${tierColorMap[dynamicTier.color_theme]}20`,
        label: dynamicTier.name,
        dealLimit: dynamicTier.deal_limit,
        monthlyPrice: dynamicTier.monthly_price,
        sortOrder: dynamicTier.sort_order,
        adminOnly: dynamicTier.admin_only
      };
    }
    return planConfigFor(tierKey, subscriptionTiers);
  };
  
  const currentTierOrder = subscriptionTiers.find(t => t.tier_key === currentPlanKey)?.sort_order || 0;
  const currentConfig = getTierConfig(currentPlanKey);
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Subscription Plan" maxWidth="max-w-md">
        <p className="text-gray-600 mb-4">
          Current plan: <span className="font-bold">{currentConfig.label}</span>
        </p>
        
        <div className="space-y-2 mb-4">
          {availableTiers.length > 0 ? (
            availableTiers.map(tierItem => {
              const tierKey = tierItem.tier_key || tierItem;
              const config = getTierConfig(tierKey);
              const tierOrder = tierItem.sort_order || 0;
              const isUpgrade = tierOrder > currentTierOrder;
              
              return (
                <label 
                  key={tierKey}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedPlan === tierKey ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input 
                    type="radio" 
                    name="plan" 
                    value={tierKey}
                    checked={selectedPlan === tierKey}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="sr-only"
                  />
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />
                  <span className="font-medium">{config.label}</span>
                  <span className="text-xs text-gray-500">
                    ({config.dealLimit} deal{config.dealLimit !== 1 ? 's' : ''})
                    {config.monthlyPrice !== undefined && ` • $${config.monthlyPrice}/mo`}
                  </span>
                  {config.adminOnly && (
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Admin Only</span>
                  )}
                  {isUpgrade ? (
                    <ArrowUpCircle size={16} className="ml-auto text-green-500" />
                  ) : (
                    <ArrowDownCircle size={16} className="ml-auto text-amber-500" />
                  )}
                </label>
              );
            })
          ) : (
            <p className="text-gray-500 text-center py-4">No other plans available</p>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button onClick={onClose} className="flex-1 rounded-xl" variant="outline" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={() => onConfirm(selectedPlan)} 
            className="flex-1 rounded-xl"
            style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
            disabled={loading || !selectedPlan}
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Change Plan
          </Button>
        </div>
    </Modal>
  );
};

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [archiveModal, setArchiveModal] = useState({ isOpen: false });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingAvatar, setDeletingAvatar] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Business attachment state
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [attachingBusiness, setAttachingBusiness] = useState(false);
  const [detachingBusiness, setDetachingBusiness] = useState(false);
  
  // Venue Partner state
  const [venuePartnerData, setVenuePartnerData] = useState(null);
  const [venuePartnerLocation, setVenuePartnerLocation] = useState(null);
  
  // Subscription management state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionWarningModal, setSubscriptionWarningModal] = useState({ isOpen: false, action: '' });
  const [cancelSubscriptionModal, setCancelSubscriptionModal] = useState(false);
  const [changePlanModal, setChangePlanModal] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const getPlanConfig = (tierKey) => planConfigFor(tierKey, subscriptionTiers);
  
  // Admin promotion state
  const [promoteModal, setPromoteModal] = useState(false);
  const [promotingAdmin, setPromotingAdmin] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
    fetchBusinesses();
    fetchSubscriptionTiers();
  }, [customerId]);

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await adminApi.get(`/admin/subscription-tiers`);
      
      const tiers = response.data.tiers || [];
      console.log('Subscription tiers loaded:', tiers.length);
      setSubscriptionTiers(tiers);
    } catch (err) {
      console.error('Error fetching subscription tiers:', err);
      // Set fallback tiers on error
      setSubscriptionTiers([
          { tier_key: 'free', name: 'Free', deal_limit: 1, hub_cap: null, monthly_price: 0, sort_order: 0, color_theme: 'gray', admin_only: true, is_enabled: true },
          { tier_key: 'local', name: 'Local', deal_limit: 1, hub_cap: 1, monthly_price: 125, annual_price: 1250, sort_order: 1, color_theme: 'orange', is_enabled: true },
          { tier_key: 'multi', name: 'Multi', deal_limit: 3, hub_cap: 3, monthly_price: 199, annual_price: 1990, sort_order: 2, color_theme: 'purple', is_enabled: true }
        ]);
    }
  };
  
  useEffect(() => {
    // Fetch subscription info when customer is loaded and is an advertiser (check both role and account_type)
    if (customer?.account_type === 'advertiser' || customer?.role === 'advertiser') {
      fetchSubscriptionInfo();
    }
  }, [customer?.account_type, customer?.role, customerId]);
  
  useEffect(() => {
    // Fetch venue partner info when customer is a venue partner
    if (customer?.role === 'venue_partner' && customer?.venue_partner_id) {
      fetchVenuePartnerInfo();
    }
  }, [customer?.role, customer?.venue_partner_id]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await adminApi.get(`/admin/customers/${customerId}`);
      console.log('Customer data received:', response.data);
      setCustomer(response.data);
      
      // Check if user is already an admin - check role, is_admin flag, and account_type
      const userData = response.data;
      const isAdmin = userData.role === 'admin' || 
                      userData.role === 'master_admin' || 
                      userData.is_admin === true ||
                      userData.account_type === 'admin';
      setIsUserAdmin(isAdmin);
    } catch (err) {
      console.error('Error fetching customer details:', err);
      alert('Failed to load customer details');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await adminApi.get(`/admin/businesses?limit=100`);
      setBusinesses(response.data.businesses || []);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    }
  };
  
  const fetchVenuePartnerInfo = async () => {
    try {
      // Fetch venue partner details
      const vpResponse = await adminApi.get(`/venue/partner/${customer.venue_partner_id}`);
      console.log('Venue Partner data received:', vpResponse.data);
      setVenuePartnerData(vpResponse.data);
      
      // If venue partner has a hub location, fetch it
      if (vpResponse.data?.hub_location_id) {
        const locationResponse = await adminApi.get(`/locations/${vpResponse.data.hub_location_id}`);
        console.log('Venue Partner Location:', locationResponse.data);
        setVenuePartnerLocation(locationResponse.data);
      }
    } catch (err) {
      console.error('Error fetching venue partner info:', err);
    }
  };
  
  const fetchSubscriptionInfo = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await adminApi.get(`/admin/customers/${customerId}/subscription`);
      console.log('Subscription info:', response.data);
      setSubscription(response.data);
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };
  
  const handleCancelSubscription = async (immediate) => {
    setCancelingSubscription(true);
    try {
      const response = await adminApi.post(
        `/admin/customers/${customerId}/subscription/cancel?immediate=${immediate}`,
        {}
      );
      
      setMessage({ type: 'success', text: response.data.message });
      setCancelSubscriptionModal(false);
      fetchSubscriptionInfo();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to cancel subscription' 
      });
    } finally {
      setCancelingSubscription(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };
  
  const handleChangePlan = async (newPlan) => {
    setChangingPlan(true);
    try {
      const response = await adminApi.post(
        `/admin/customers/${customerId}/subscription/change-plan?new_plan=${newPlan}`,
        {}
      );
      
      let msg = response.data.message;
      if (response.data.paused_deals_count > 0) {
        msg += ` (${response.data.paused_deals_count} excess deals were paused)`;
      }
      
      setMessage({ type: 'success', text: msg });
      setChangePlanModal(false);
      fetchSubscriptionInfo();
    } catch (err) {
      console.error('Error changing plan:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to change plan' 
      });
    } finally {
      setChangingPlan(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleAttachBusiness = async () => {
    if (!selectedBusinessId) {
      setMessage({ type: 'error', text: 'Please select a business' });
      return;
    }
    
    setAttachingBusiness(true);
    try {
      const response = await adminApi.patch(
        `/admin/customers/${customerId}/attach-business?business_id=${selectedBusinessId}`,
        {}
      );
      
      setMessage({ type: 'success', text: response.data.message });
      // Refresh customer data
      fetchCustomerDetails();
      setSelectedBusinessId('');
    } catch (err) {
      console.error('Error attaching business:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to attach business' });
    } finally {
      setAttachingBusiness(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleDetachBusiness = async () => {
    if (!window.confirm('Are you sure you want to detach this user from their business? They will lose access to the Business Dashboard.')) {
      return;
    }
    
    setDetachingBusiness(true);
    try {
      await adminApi.patch(
        `/admin/customers/${customerId}/detach-business`,
        {}
      );
      
      setMessage({ type: 'success', text: 'User detached from business successfully' });
      fetchCustomerDetails();
    } catch (err) {
      console.error('Error detaching business:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to detach business' });
    } finally {
      setDetachingBusiness(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleResetPassword = async () => {
    if (!confirm('Send password reset email to this customer?')) return;
    
    setResetLoading(true);
    try {
      await adminApi.post(
        `/admin/customers/${customerId}/reset-password`,
        {}
      );
      alert('Password reset email sent successfully!');
    } catch (err) {
      console.error('Error sending reset email:', err);
      alert('Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handleArchiveCustomer = async () => {
    setArchiving(true);
    try {
      await adminApi.patch(
        `/admin/customers/${customerId}/archive?archived=${!customer.archived}`,
        {}
      );
      setArchiveModal({ isOpen: false });
      alert(`Customer ${customer.archived ? 'restored' : 'archived'} successfully!`);
      navigate('/admin/customers');
    } catch (err) {
      console.error('Error archiving customer:', err);
      setArchiveModal({ isOpen: false });
      
      // Check if error is due to active subscription
      const detail = err.response?.data?.detail;
      if (detail?.error === 'active_subscription' || detail?.error === 'subscription_not_expired') {
        setSubscriptionWarningModal({
          isOpen: true,
          action: 'archive',
          info: detail.subscription_info
        });
      } else {
        const errorMsg = typeof detail === 'object' ? detail.message : detail;
        alert(errorMsg || 'Failed to archive customer');
      }
    } finally {
      setArchiving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    setDeleting(true);
    try {
      await adminApi.delete(
        `/admin/customers/${customerId}`
      );
      setDeleteModal({ isOpen: false });
      alert('Customer deleted successfully!');
      navigate('/admin/customers');
    } catch (err) {
      console.error('Error deleting customer:', err);
      setDeleteModal({ isOpen: false });
      
      // Check if error is due to active subscription or billing history
      const detail = err.response?.data?.detail;
      if (detail?.error === 'active_subscription') {
        setSubscriptionWarningModal({
          isOpen: true,
          action: 'delete',
          info: detail.subscription_info
        });
      } else if (detail?.error === 'has_billing_history') {
        setMessage({
          type: 'error',
          text: detail.message || 'Users with billing history cannot be deleted. Please archive instead.'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 7000);
      } else {
        const errorMsg = typeof detail === 'object' ? detail.message : detail;
        alert(errorMsg || 'Failed to delete customer');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to delete this customer\'s avatar? They will be assigned a new default avatar.')) {
      return;
    }
    
    setDeletingAvatar(true);
    try {
      
      const response = await adminApi.delete(
        `/admin/customers/${customerId}/avatar`
      );
      
      // Update customer data with new default avatar
      setCustomer({
        ...customer,
        picture: response.data.new_avatar,
        is_default_avatar: true
      });
      
      setMessage({
        type: 'success',
        text: 'Avatar deleted. Customer assigned new default avatar.'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
    } catch (error) {
      console.error('Error deleting avatar:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to delete avatar'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setDeletingAvatar(false);
    }
  };

  // Promote/Demote Admin Functions
  const handlePromoteToAdmin = async () => {
    setPromotingAdmin(true);
    try {
      await adminApi.post(
        `/admin/customers/${customerId}/promote-to-admin`,
        {}
      );
      
      setMessage({ type: 'success', text: `${customer.email} has been promoted to Master Admin!` });
      setIsUserAdmin(true);
      setPromoteModal(false);
      fetchCustomerDetails(); // Refresh data
    } catch (error) {
      console.error('Error promoting to admin:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to promote user to admin' 
      });
    } finally {
      setPromotingAdmin(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleDemoteFromAdmin = async () => {
    setPromotingAdmin(true);
    try {
      await adminApi.post(
        `/admin/customers/${customerId}/demote-from-admin`,
        {}
      );
      
      setMessage({ type: 'success', text: `${customer.email} has been demoted from Master Admin.` });
      setIsUserAdmin(false);
      setPromoteModal(false);
      fetchCustomerDetails(); // Refresh data
    } catch (error) {
      console.error('Error demoting from admin:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to demote user from admin' 
      });
    } finally {
      setPromotingAdmin(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateEST(dateString);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin" size={48} style={{ color: '#6F5BEB' }} />
        </div>
      </AdminLayout>
    );
  }

  if (!customer) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Customer not found</p>
          <Button onClick={() => navigate('/admin/customers')} className="mt-4">
            Back to Customers
          </Button>
        </div>
      </AdminLayout>
    );
  }

  // Get accent color based on user type
  const getAccentColor = () => {
    // Check multiple conditions for admin status - no hardcoded emails
    const isAdmin = customer?.role === 'admin' || customer?.role === 'master_admin' || 
                    customer?.account_type === 'admin' || customer?.is_admin === true;
    const isVenue = customer?.role === 'venue_partner';
    const isAdvertiser = customer?.role === 'advertiser' || customer?.account_type === 'advertiser';
    
    if (isAdmin) return 'indigo';
    if (isVenue) return 'venue';
    if (isAdvertiser) return 'business';
    return 'consumer';
  };
  
  const accentColor = getAccentColor();

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* V5 Back Link */}
        <button
          onClick={() => navigate('/admin/customers')}
          className="flex items-center gap-2 text-navy/60 hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Customers
        </button>
        
        {/* V5 Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <p className="font-mono text-consumer text-sm mb-1">// admin.users.detail</p>
            <h1 className="font-display text-3xl text-navy">
              {customer.first_name} {customer.last_name}
            </h1>
            <p className="text-navy/50 break-all">{customer.email}</p>
            <div className="mt-3">
              <UserTypeBadge accountType={customer.account_type} role={customer.role} email={customer.email} size="large" />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Admin Promote/Demote Button */}
            {customer.email !== 'rauf@iolab.co' && (
              <button
                onClick={() => setPromoteModal(true)}
                className={`px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-all ${
                  isUserAdmin 
                    ? 'bg-amber-500 text-white hover:shadow-lg hover:shadow-amber-500/20' 
                    : 'bg-indigo-600 text-white hover:shadow-lg hover:shadow-indigo-600/20'
                }`}
              >
                {isUserAdmin ? (
                  <>
                    <ArrowDownCircle size={16} />
                    <span className="hidden sm:inline">Demote Admin</span>
                    <span className="sm:hidden">Demote</span>
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    <span className="hidden sm:inline">Make Admin</span>
                    <span className="sm:hidden">Admin</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="px-4 py-2.5 bg-business text-white rounded-xl font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-business/20 transition-all disabled:opacity-50"
            >
              <KeyRound size={16} />
              <span className="hidden sm:inline">{resetLoading ? 'Sending...' : 'Reset Password'}</span>
              <span className="sm:hidden">Reset</span>
            </button>
            <button
              onClick={() => setArchiveModal({ isOpen: true })}
              className="px-4 py-2.5 bg-consumer text-white rounded-xl font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-consumer/20 transition-all"
            >
              <Archive size={16} />
              {customer.archived ? 'Restore' : 'Archive'}
            </button>
            <button
              onClick={() => setDeleteModal({ isOpen: true })}
              className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium flex items-center gap-2 hover:shadow-lg hover:shadow-red-500/20 transition-all"
            >
              <Trash2 size={16} />
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>

        {/* Success/Error Message */}
        {message.text && (
          <div className={`p-4 rounded-xl ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* V5 Two-Column Card: Profile Picture + Contact Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-navy/5">
            {/* Profile Picture */}
            <div className="p-6">
              <h3 className="font-display text-lg text-navy mb-4">Profile Picture</h3>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-cream border-2 border-navy/10 shadow-sm flex items-center justify-center overflow-hidden">
                  {customer.picture ? (
                    <img
                      src={resolveAssetUrl(customer.picture)}
                      alt={customer.first_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={48} className="text-navy/30" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  {/* Avatar Type Badge */}
                  <div>
                    <span 
                      className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${
                        customer.is_default_avatar 
                          ? 'bg-gray-100 text-gray-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {customer.is_default_avatar ? '📦 Default Avatar' : '✨ Custom Avatar'}
                    </span>
                  </div>
                  
                  {/* Delete Button - only show if custom avatar */}
                  {!customer.is_default_avatar && (
                    <button
                      onClick={handleDeleteAvatar}
                      disabled={deletingAvatar}
                      className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Trash2 size={16} className="mr-2" />
                      {deletingAvatar ? 'Deleting...' : 'Delete Avatar'}
                    </button>
                  )}
                  
                  {/* Info text */}
                  <p className="text-xs text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {customer.is_default_avatar 
                      ? 'Customer is using a default avatar icon' 
                      : 'Deleting will assign a new random default avatar'}
                  </p>
                </div>
              </div>
            </div>
            {/* Contact Information - Part of the split card */}
            <div className="p-6">
              <h3 className="font-display text-lg text-navy mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-consumer/10 rounded-lg flex items-center justify-center">
                    <Mail size={18} className="text-consumer" />
                  </div>
                  <div>
                    <p className="text-sm text-navy/50">Email</p>
                    <p className="font-semibold text-navy">{customer.email}</p>
                  </div>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-venue/10 rounded-lg flex items-center justify-center">
                      <Phone size={18} className="text-venue" />
                    </div>
                    <div>
                      <p className="text-sm text-navy/50">Phone</p>
                      <p className="font-semibold text-navy">{customer.phone}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-business/10 rounded-lg flex items-center justify-center">
                    <Calendar size={18} className="text-business" />
                  </div>
                  <div>
                    <p className="text-sm text-navy/50">Joined</p>
                    <p className="font-semibold text-navy">{formatDate(customer.created_at)}</p>
                  </div>
                </div>
                {customer.last_login && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-navy/5 rounded-lg flex items-center justify-center">
                      <Calendar size={18} className="text-navy/50" />
                    </div>
                    <div>
                      <p className="text-sm text-navy/50">Last Login</p>
                      <p className="font-semibold text-navy">{formatDate(customer.last_login)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

          {/* V5 User Type & Business Access Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6">
            <h3 className="font-display text-lg text-navy mb-4">User Type & Business Access</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Status */}
              <div className="p-4 bg-cream rounded-xl">
                <h4 className="font-semibold text-navy mb-3">Current Status</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-navy/60">User Type:</span>
                    <UserTypeBadge accountType={customer.account_type} role={customer.role} email={customer.email} />
                  </div>
                  
                  {customer.business_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-navy/60">Attached Business:</span>
                      <span className="font-semibold text-consumer">
                        {businesses.find(b => b.id === customer.business_id)?.name || customer.business_name || 'Unknown Business'}
                      </span>
                    </div>
                  )}
                  
                  {customer.account_type === 'advertiser' && customer.business_id && (
                    <button
                      onClick={handleDetachBusiness}
                      disabled={detachingBusiness}
                      className="w-full mt-2 px-4 py-2 bg-red-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Unlink size={16} />
                      {detachingBusiness ? 'Detaching...' : 'Detach from Business'}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Attach to Business */}
              <div className="p-4 bg-consumer/5 rounded-xl border border-consumer/20">
                <h4 className="font-semibold text-navy mb-3">Attach to Business</h4>
                <p className="text-sm text-navy/60 mb-3">
                  Attaching a user to a business will give them Advertiser access and the ability to manage that business.
                </p>
                
                <div className="space-y-3">
                  <select
                    value={selectedBusinessId}
                    onChange={(e) => setSelectedBusinessId(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-consumer/30 rounded-xl text-navy focus:outline-none focus:border-consumer transition-colors"
                  >
                    <option value="">Select a business...</option>
                    {businesses.map(business => (
                      <option key={business.id} value={business.id}>
                        {business.name} ({business.plan_tier?.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={handleAttachBusiness}
                    disabled={attachingBusiness || !selectedBusinessId}
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-consumer to-amber-400 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-lg hover:shadow-consumer/20 transition-all"
                  >
                    <Link2 size={16} />
                    {attachingBusiness ? 'Attaching...' : 'Attach to Business'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* V5 Venue Partner Details - Only for Venue Partners */}
          {customer.role === 'venue_partner' && (
            <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-venue/10 rounded-lg flex items-center justify-center">
                  <MapPin size={20} className="text-venue" />
                </div>
                <h3 className="font-display text-lg text-navy">Venue Partner Details</h3>
              </div>
              
              {venuePartnerData ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Venue Info */}
                  <div className="p-4 bg-venue/5 rounded-xl">
                    <h4 className="font-semibold text-navy mb-3">Venue Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Venue Name:</span>
                        <span className="font-semibold text-navy">{venuePartnerData.venue_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Venue Type:</span>
                        <span className="font-semibold text-navy">{venuePartnerData.venue_type || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          venuePartnerData.status === 'approved' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {venuePartnerData.status?.charAt(0).toUpperCase() + venuePartnerData.status?.slice(1)}
                        </span>
                      </div>
                      {venuePartnerData.phone && (
                        <div className="flex items-center justify-between">
                          <span className="text-navy/60">Phone:</span>
                          <span className="font-semibold text-navy">{venuePartnerData.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Attached Hub */}
                  <div className="p-4 bg-consumer/5 rounded-xl border border-consumer/20">
                    <h4 className="font-semibold text-navy mb-3">Attached Hub Location</h4>
                    {venuePartnerLocation ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-navy/60">Hub Name:</span>
                          <span className="font-semibold text-venue">{venuePartnerLocation.name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-navy/60">Address:</span>
                          <span className="font-semibold text-navy text-sm text-right">{venuePartnerLocation.address}</span>
                        </div>
                        {venuePartnerLocation.city && (
                          <div className="flex items-center justify-between">
                            <span className="text-navy/60">City/State:</span>
                            <span className="font-semibold text-navy">{venuePartnerLocation.city}, {venuePartnerLocation.state}</span>
                          </div>
                        )}
                        <button
                          onClick={() => navigate(`/admin/locations/${venuePartnerLocation.id}`)}
                          className="w-full mt-2 px-4 py-2.5 bg-gradient-to-r from-venue to-teal-400 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-venue/20 transition-all"
                        >
                          <MapPin size={16} />
                          View Hub Details
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-navy/50">No hub location attached</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-venue" size={32} />
                </div>
              )}
            </div>
          )}

          {/* V5 Subscription Management - Only for Advertisers */}
          {(customer.role === 'advertiser' || customer.account_type === 'advertiser') && (
            <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-business/10 rounded-lg flex items-center justify-center">
                  <CreditCard size={20} className="text-business" />
                </div>
                <h3 className="font-display text-lg text-navy">Subscription Management</h3>
              </div>
              
              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-business" size={32} />
                </div>
              ) : subscription ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Subscription Info */}
                  <div className="p-4 bg-business/5 rounded-xl">
                    <h4 className="font-semibold text-navy mb-3">Subscription Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Business:</span>
                        <span className="font-semibold text-navy">{subscription.business_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Plan:</span>
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: getPlanConfig(subscription.plan_tier).bg,
                            color: getPlanConfig(subscription.plan_tier).color,
                          }}
                        >
                          {getPlanConfig(subscription.plan_tier).label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-navy/60">Status:</span>
                        <SubscriptionStatusBadge status={subscription.subscription_status} />
                      </div>
                      {subscription.subscription_end_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-navy/60">Ends:</span>
                          <span className="font-semibold text-amber-600">
                            {formatDateOnlyEST(subscription.subscription_end_date)}
                          </span>
                        </div>
                      )}
                      {subscription.subscription_updated_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-navy/60">Last Updated:</span>
                          <span className="text-sm text-navy/50">
                            {formatDateOnlyEST(subscription.subscription_updated_at)}
                          </span>
                        </div>
                      )}
                      
                      {/* Billing History Warning */}
                      {subscription.has_billing_history && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                          <div className="flex items-start gap-2">
                            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                            <p className="text-xs text-amber-800">
                              This user has billing history and cannot be deleted. They can only be archived.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Subscription Actions */}
                  <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                    <h4 className="font-semibold text-navy mb-3">Actions</h4>
                    <div className="space-y-3">
                      {/* Change Plan Button */}
                      <button
                        onClick={() => setChangePlanModal(true)}
                        className="w-full px-4 py-2.5 bg-gradient-to-r from-business to-purple-400 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-business/20 transition-all disabled:opacity-50"
                        disabled={subscription.subscription_status === 'pending_cancel'}
                      >
                        <ArrowUpCircle size={16} />
                        Change Plan
                      </button>
                      
                      {/* Cancel Subscription Button */}
                      {subscription.has_subscription && subscription.subscription_status === 'active' && (
                        <button
                          onClick={() => setCancelSubscriptionModal(true)}
                          className="w-full px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-red-500/20 transition-all"
                        >
                          <XCircle size={16} />
                          Cancel Subscription
                        </button>
                      )}
                      
                      {subscription.subscription_status === 'pending_cancel' && (
                        <div className="p-3 bg-amber-50 rounded-xl">
                          <p className="text-sm text-amber-800">
                            Subscription is scheduled to cancel on {formatDateOnlyEST(subscription.subscription_end_date)}.
                          </p>
                        </div>
                      )}
                      
                      {(!subscription.plan_tier || subscription.plan_tier === 'free') && subscription.subscription_status !== 'active' && (
                        <div className="p-3 bg-navy/5 rounded-xl">
                          <p className="text-sm text-navy/60">
                            No active paid subscription.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-cream rounded-xl text-center">
                  <p className="text-navy/60">No subscription information available.</p>
                </div>
              )}
            </div>
          )}

          {/* V5 Activity Stats - 8 Mini Stat Cards */}
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6">
            <h3 className="font-display text-lg text-navy mb-4">Activity Statistics</h3>
            
            {/* Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-venue/5 rounded-xl p-4">
                <div className="w-8 h-8 bg-venue/10 rounded-lg flex items-center justify-center mb-2">
                  <Scan size={16} className="text-venue" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.total_scans || 0}</p>
                <p className="text-sm text-navy/50">Scans</p>
              </div>
              <div className="bg-consumer/5 rounded-xl p-4">
                <div className="w-8 h-8 bg-consumer/10 rounded-lg flex items-center justify-center mb-2">
                  <Gift size={16} className="text-consumer" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.total_claims}</p>
                <p className="text-sm text-navy/50">Claims</p>
              </div>
              <div className="bg-business/5 rounded-xl p-4">
                <div className="w-8 h-8 bg-business/10 rounded-lg flex items-center justify-center mb-2">
                  <Award size={16} className="text-business" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.total_redeemed}</p>
                <p className="text-sm text-navy/50">Redeems</p>
              </div>
              <div className="bg-navy/5 rounded-xl p-4">
                <div className="w-8 h-8 bg-navy/10 rounded-lg flex items-center justify-center mb-2">
                  <Calendar size={16} className="text-navy/60" />
                </div>
                <p className="text-sm font-bold text-navy">{formatDateOnlyEST(customer.created_at)}</p>
                <p className="text-sm text-navy/50">Joined</p>
              </div>
            </div>
            
            {/* Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-amber-50 rounded-xl p-4">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mb-2">
                  <MapPin size={16} className="text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.hubs_visited || 0}</p>
                <p className="text-sm text-navy/50">Hubs Visited</p>
              </div>
              <div className="bg-pink-50 rounded-xl p-4">
                <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center mb-2">
                  <Eye size={16} className="text-pink-600" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.pitch_deck_view_count}</p>
                <p className="text-sm text-navy/50">Pitch Views</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                  <CheckCircle size={16} className="text-green-600" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.nda_agreed ? 'Yes' : 'No'}</p>
                <p className="text-sm text-navy/50">NDA Signed</p>
              </div>
              <div className="bg-indigo-50 rounded-xl p-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mb-2">
                  <Newspaper size={16} className="text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-navy">{customer.newsletter_subscribed ? 'Yes' : 'No'}</p>
                <p className="text-sm text-navy/50">Newsletter</p>
              </div>
            </div>
          </div>

        {/* V5 Recent Claims */}
        {customer.recent_claims && customer.recent_claims.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6">
            <h3 className="font-display text-lg text-navy mb-4">
              Recent Claims ({customer.recent_claims.length})
            </h3>
            <div className="space-y-3">
              {customer.recent_claims.map((claim, idx) => (
                <div key={claim.id} className={`flex justify-between items-center p-4 rounded-xl ${idx % 2 === 0 ? 'bg-cream' : 'bg-navy/5'}`}>
                  <div>
                    <p className="font-semibold text-navy">{claim.business_name}</p>
                    <p className="text-sm text-navy/60">{claim.offer || claim.deal_title}</p>
                    <p className="text-xs text-navy/40 mt-1">{formatDate(claim.claimed_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-lg text-consumer">{claim.code}</p>
                    <p className={`text-sm ${claim.redeemed ? 'text-green-600' : 'text-navy/50'}`}>
                      {claim.redeemed ? 'Redeemed' : 'Active'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveModal.isOpen}
        onClose={() => setArchiveModal({ isOpen: false })}
        onConfirm={handleArchiveCustomer}
        title={customer.archived ? 'Restore Customer?' : 'Archive Customer?'}
        message={`Are you sure you want to ${customer.archived ? 'restore' : 'archive'} ${customer.first_name} ${customer.last_name}?`}
        confirmText={customer.archived ? 'Restore' : 'Archive'}
        confirmStyle={{ 
          backgroundColor: customer.archived ? '#10B981' : '#F59E0B',
          color: '#FFFFFF' 
        }}
        loading={archiving}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false })}
        onConfirm={handleDeleteCustomer}
        title="Delete Customer?"
        message={`⚠️ PERMANENT DELETE: Are you sure you want to permanently delete ${customer.first_name} ${customer.last_name}? This will delete all their claims and cannot be undone.`}
        confirmText="Delete Permanently"
        confirmStyle={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
        loading={deleting}
      />
      
      {/* Active Subscription Warning Modal */}
      <ActiveSubscriptionWarningModal
        isOpen={subscriptionWarningModal.isOpen}
        onClose={() => setSubscriptionWarningModal({ isOpen: false, action: '', info: null })}
        subscriptionInfo={subscriptionWarningModal.info}
        action={subscriptionWarningModal.action}
      />
      
      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={cancelSubscriptionModal}
        onClose={() => setCancelSubscriptionModal(false)}
        onConfirm={handleCancelSubscription}
        subscriptionInfo={subscription}
        loading={cancelingSubscription}
      />
      
      {/* Change Plan Modal */}
      <ChangePlanModal
        isOpen={changePlanModal}
        onClose={() => setChangePlanModal(false)}
        onConfirm={handleChangePlan}
        currentPlan={subscription?.plan_tier}
        loading={changingPlan}
        subscriptionTiers={subscriptionTiers}
      />
      
      {/* Admin Promote/Demote Modal */}
      {promoteModal && (
        <div className="fixed inset-0 bg-navy/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isUserAdmin ? 'bg-amber-100' : 'bg-indigo-100'
              }`}>
                <Shield size={32} className={isUserAdmin ? 'text-amber-600' : 'text-indigo-600'} />
              </div>
              <h3 className="font-display text-xl text-navy mb-2">
                {isUserAdmin ? 'Demote from Master Admin?' : 'Promote to Master Admin?'}
              </h3>
              <p className="text-navy/60">
                {isUserAdmin ? (
                  <>
                    This will remove <strong>{customer.email}</strong>&apos;s master admin privileges. 
                    They will no longer have access to the admin dashboard.
                  </>
                ) : (
                  <>
                    This will grant <strong>{customer.email}</strong> full master admin privileges, 
                    including access to all admin features, user management, and system settings.
                  </>
                )}
              </p>
            </div>
            
            {!isUserAdmin && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-amber-800">
                    <strong>Important:</strong> Master admins have full system access including the ability to:
                    <ul className="list-disc ml-4 mt-2 space-y-1">
                      <li>View and manage all users</li>
                      <li>Access all business data</li>
                      <li>Modify system settings</li>
                      <li>Promote/demote other admins</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setPromoteModal(false)}
                className="flex-1 px-4 py-3 border border-navy/10 text-navy/70 rounded-xl font-medium hover:bg-navy/5 transition-colors"
                disabled={promotingAdmin}
              >
                Cancel
              </button>
              <button
                onClick={isUserAdmin ? handleDemoteFromAdmin : handlePromoteToAdmin}
                disabled={promotingAdmin}
                className={`flex-1 px-4 py-3 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${
                  isUserAdmin 
                    ? 'bg-amber-500 hover:bg-amber-600' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {promotingAdmin ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : isUserAdmin ? (
                  <>
                    <ArrowDownCircle size={18} />
                    Demote Admin
                  </>
                ) : (
                  <>
                    <Shield size={18} />
                    Make Admin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CustomerDetails;
